import { useCallback, useEffect, useRef, useState } from 'react'
import { WherebyRoomConnectionStatus } from '../../../../../../pure-js/enums/Whereby'

const LEAVE_COMPLETION_TIMEOUT_MS = 8_000 // 8 s

const isLeaveCompleteStatus = (connectionStatus: WherebyRoomConnectionStatus): boolean => {
  switch (connectionStatus) {
    case WherebyRoomConnectionStatus.KICKED:
    case WherebyRoomConnectionStatus.KNOCK_REJECTED:
    case WherebyRoomConnectionStatus.LEFT:
    case WherebyRoomConnectionStatus.ROOM_LOCKED:
      return true
    default:
      return false
  }
}

interface WherebyCallLifecycleParams {
  displayName: string
  canJoinRoom: boolean
  connectionStatus?: WherebyRoomConnectionStatus
  joinRoom: () => Promise<unknown>
  leaveRoom: () => void
  stopLocalMedia: () => void
  setDisplayName: (displayName: string) => void
  onLeaveComplete?: () => void
}

interface WherebyCallLifecycle {
  requestLeave: () => void
}

// Keeps one room generation mounted until signaling teardown finishes.
export const useWherebyCallLifecycle = ({
  displayName,
  canJoinRoom,
  connectionStatus,
  joinRoom,
  leaveRoom,
  stopLocalMedia,
  setDisplayName,
  onLeaveComplete
}: WherebyCallLifecycleParams): WherebyCallLifecycle => {
  const [isLeaveRequested, setIsLeaveRequested] = useState(false)
  const hasRequestedJoinRef = useRef(false)
  const leaveRequestedRef = useRef(false)
  const leaveCompletedRef = useRef(false)
  const joinRoomRef = useRef(joinRoom)
  const leaveRoomRef = useRef(leaveRoom)
  const stopLocalMediaRef = useRef(stopLocalMedia)
  const setDisplayNameRef = useRef(setDisplayName)
  const onLeaveCompleteRef = useRef(onLeaveComplete)

  joinRoomRef.current = joinRoom
  leaveRoomRef.current = leaveRoom
  stopLocalMediaRef.current = stopLocalMedia
  setDisplayNameRef.current = setDisplayName
  onLeaveCompleteRef.current = onLeaveComplete

  const completeLeave = useCallback(() => {
    if (leaveCompletedRef.current) return
    leaveCompletedRef.current = true
    onLeaveCompleteRef.current?.()
  }, [])

  const leaveCurrentRoom = useCallback(() => {
    try {
      leaveRoomRef.current()
    } catch {
      completeLeave()
    }
  }, [completeLeave])

  // A call surface represents exactly one room generation. Room changes remount the surface by room URL.
  useEffect(() => {
    if (!displayName || !canJoinRoom || isLeaveRequested || hasRequestedJoinRef.current) return
    hasRequestedJoinRef.current = true
    void joinRoomRef
      .current()
      .then(() => {
        // A server-confirmed join can win the race with a hangup requested while connecting.
        if (leaveRequestedRef.current && !leaveCompletedRef.current) leaveCurrentRoom()
      })
      .catch(() => undefined)
  }, [canJoinRoom, displayName, isLeaveRequested, leaveCurrentRoom])

  // Names may arrive/change after connection. Updating them must never restart signaling.
  useEffect(() => {
    if (!displayName || leaveCompletedRef.current) return
    try {
      setDisplayNameRef.current(displayName)
    } catch {}
  }, [displayName])

  useEffect(() => {
    if (!isLeaveRequested || !connectionStatus || !isLeaveCompleteStatus(connectionStatus)) return
    completeLeave()
  }, [completeLeave, connectionStatus, isLeaveRequested])

  // Backstop for a server that never acknowledges leave. SDK hook cleanup performs the final teardown.
  useEffect(() => {
    if (!isLeaveRequested) return
    const timeoutId = window.setTimeout(completeLeave, LEAVE_COMPLETION_TIMEOUT_MS)
    return () => window.clearTimeout(timeoutId)
  }, [completeLeave, isLeaveRequested])

  const requestLeave = useCallback(() => {
    if (leaveRequestedRef.current || leaveCompletedRef.current) return
    leaveRequestedRef.current = true
    setIsLeaveRequested(true)
    try {
      stopLocalMediaRef.current()
    } catch {}
    leaveCurrentRoom()
  }, [leaveCurrentRoom])

  return { requestLeave }
}
